import ImageThumbnail from '@/components/media/ImageThumbnail'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { getLineItemDisplay } from '@/lib/line-item-display'
import { getRestoredRows } from '@/lib/pos/order-change'
import { useIsCartMutating } from '@/lib/services/carts/carts.query'
import type { OrderChangeRow } from '@/lib/services/carts/carts.types'
import { useRestoreSalesOrderChangeItem } from '@/lib/services/sales-orders/sales-order-changes.query'
import { formatCurrency, getErrorMessage } from '@/lib/utils'
import { RotateCcw } from 'lucide-react'
import { toast } from 'sonner'

type RemovedOrderItemRowProps = {
  row: OrderChangeRow
  /** Every item the change removed, since restoring one can bring back its item or add-ons too. */
  removed: OrderChangeRow[]
  salesOrderId: number
  cartId: number
  /** The cart is locked (a payment toward the change is open): nothing comes back until it resolves. */
  disabled?: boolean
}

/** An order item a change takes off, crossed out where its cart line stood, with a way to put it back. */
export function RemovedOrderItemRow({
  row,
  removed,
  salesOrderId,
  cartId,
  disabled = false,
}: RemovedOrderItemRowProps) {
  const isCartMutating = useIsCartMutating()
  const restoreItem = useRestoreSalesOrderChangeItem()
  const display = getLineItemDisplay(
    row.restore ? { variant_details: row.restore.variant_details } : null
  )
  const name = row.name ?? display.primaryName
  const quantity = row.quantity_before ?? 1

  const restore = async () => {
    if (row.sales_order_item_id === null) {
      return
    }
    try {
      await restoreItem({
        salesOrderId,
        cartId,
        salesOrderItemId: row.sales_order_item_id,
        preview: getRestoredRows(removed, row),
      })
    } catch (error) {
      toast.error('Unable to restore this item', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  return (
    <div className="@container border-b p-2 last:border-b-0">
      {/* Laid out as CartItemRow is: Restore where edit and remove sit, the price where the line's price is. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 @[36rem]:flex @[36rem]:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3 @[22rem]:col-span-2">
          {display.image ? (
            <ImageThumbnail media={display.image} className="h-12 w-12 shrink-0 shadow-none" />
          ) : null}
          <div className="min-w-0 flex-1">
            <Typography
              variant="label"
              tone="muted"
              decoration="lineThrough"
              className="line-clamp-2"
            >
              {name}
            </Typography>
            <div className="flex min-w-0 items-center gap-1.5">
              <Badge variant="destructiveSoft" size="xs">
                Removed
              </Badge>
              {quantity !== 1 ? (
                <Typography variant="label" tone="muted">
                  {`Qty ${quantity}`}
                </Typography>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center self-start @[22rem]:col-start-2 @[22rem]:row-start-2 @[22rem]:self-auto @[36rem]:order-last">
          {row.restore ? (
            <Button
              variant="ghost"
              size="micro"
              onClick={restore}
              disabled={disabled || isCartMutating > 0}
              aria-label={`Restore ${name}`}
            >
              <RotateCcw className="size-3.5" />
              Restore
            </Button>
          ) : null}
        </div>
        <Typography
          variant="labelStrong"
          tone="muted"
          decoration="lineThrough"
          align="right"
          className="col-span-2 @[22rem]:col-span-1 @[22rem]:col-start-1 @[22rem]:row-start-2 @[36rem]:shrink-0"
        >
          {formatCurrency(row.amount_before ?? 0)}
        </Typography>
      </div>
    </div>
  )
}
